<template>
	<div>
		<div class="setting_ps px-7 pt-4 display-flex flex-column flex-justify-space-between overflow-y-auto">
			<Col>
				<WorkspaceAvatarSelector class="mb-6" :account="account" @onFileSelected="onFileSelected" :canEdit="hasPermissionToChangeAccount" />
				<v-row no-gutters>
					<v-col cols="12">
						<h4 class="reg my-1">Workspace settings</h4>
					</v-col>
					<v-col cols="12 mt-4">
						<h5 class="my-1">Workspace name</h5>
						<AppSecondaryTextField :disabled="!hasPermissionToChangeAccount" placeholder="Workspace Name" v-model="orgName" />
					</v-col>
					<v-col cols="12 mt-4">
						<h5 class="my-1">Timezone</h5>
						<SelectTimezone :userTimezone.sync="timezone" />
					</v-col>
					<v-col cols="12 mt-4">
						<h5 class="my-1">Default role for new members</h5>
						<RoleSelector
							maxWidth="100%"
							:value="inviteUserRole"
							:disabledRoles="disabledRoles"
							:onChange="(role) => (inviteUserRole = role)"
						/>
					</v-col>
					<v-col cols="12 mt-4">
						<h2>Slack integration</h2>
						<p class="explanation mt-2" v-if="account.slack_integration?.team_name">
							Connected by <b>{{ account.slack_integration?.connected_by?.first_name }}</b> to the Slack workspace
							<b
								><span class="semi">{{ account.slack_integration.team_name }}</span></b
							>. <span style="text-decoration: underline; cursor: pointer" @click="connectSlack()">Change</span>
						</p>

						<PrimaryButton v-else @click="connectSlack">Connect Slack</PrimaryButton>
					</v-col>

					<v-col cols="12 mt-4" v-if="isNotificationsEnabled && localChannelAuth.length">
						<div v-for="item in localChannelAuth" :key="item.service + item.service_version">
							<h5 class="my-1">{{ item.name }} connection for internal notifications</h5>
							<ProfileSelector
								:serviceId="item.service"
								:serviceVersionId="item.service_version"
								:profileId="item.credId"
								@select-profile="(value) => (item.credId = value)"
								:readOnlyCred="item.credData"
								:shouldOpenProjectOptions="false"
							/>
						</div>
					</v-col>
					<v-col cols="12 mt-4">
						<h5 class="my-1">Task log data retention</h5>
						<AppSecondaryTextField disabled :placeholder="dataLifetimeHumanReadable" />
						<p class="explanation mt-2">Contact support to request a custom retention period</p>
					</v-col>
				</v-row>
				<v-row class="fill-height">
					<v-col class="text-right">
						<SecondaryButton class="mb-1" :disabled="isLoadingSave" :loading="isLoadingSave" @click="save">Save</SecondaryButton>
					</v-col>
				</v-row>
			</Col>
			<Row class="footer-container py-6 flex-align-center flex-justify-space-between">
				<h5>Danger zone</h5>
				<Row class="flex-align-center">
					<AnemicButton @click="openDeleteWorkspace" v-if="isAccountOwner" class="pa-0 mr-4 delete-button" tile>
						Delete workspace
					</AnemicButton>
					<AnemicButton @click="openLeaveWorkspace" class="pa-0 leave-button" tile>Leave workspace</AnemicButton>
				</Row>
			</Row>
		</div>
	</div>
</template>

<script>
import timezoneMoment from 'moment-timezone';

import { AccountPermissions } from '@ply-io/ply-front-shared-utilities';

import RoleSelector from '@/components/ReactComponentsDataProviders/RoleSelector.vue';
import SelectTimezone from '@/components/SelectTimezone.vue';
import AppWorkspaceAvatar from '@/components/WorkspaceSelectionMenu/AppWorkspaceAvatar.vue';
import WorkspaceSelectionMenuMixin from '@/components/WorkspaceSelectionMenu/mixins/WorkspaceSelectionMenuMixin.js';
import ProfileSelector from '@/components/common/ProfileSelector/ProfileSelector.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { APP_CONFIRMATION_DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-confirmation-dialog.const.js';
import { updateAccount, saveWorkspaceAvatar, sendDeleteAccountMail, getActiveAccount } from '@/store/modules/account/actions';
import { account, credByServiceVersionId, hasAccountPermission, dataLifetimeHumanReadable } from '@/store/modules/account/getters';
import { getService } from '@/store/modules/apps/getters';
import { isAccountOwner } from '@/store/modules/auth/getters';
import { isNotificationsEnabled } from '@/store/modules/featureFlags/getters';
import { fetchChannels } from '@/store/modules/notifications/actions';
import { channelAuthInfo } from '@/store/modules/notifications/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
const PERMISSION_DENIED_ERROR = 'permission_denied';
import { AccountRoles } from '@/utils/account-service.const';
import WorkspaceAvatarSelector from '@/views/settings/WorkspaceAvatarSelector.vue';
import { SLACK_CONNECTED_EVENT, SLACK_BROADCAST_CHANNEL_NAME } from './consts';
const bc = new BroadcastChannel(SLACK_BROADCAST_CHANNEL_NAME);
export default {
	name: 'SettingsOrganization',
	mixins: [WorkspaceSelectionMenuMixin],
	components: {
		ProfileSelector,
		WorkspaceAvatarSelector,
		SelectTimezone,
		RoleSelector,
	},
	data: () => ({
		messageHandler: null,
		deactivateDialog: false,
		firstDeleteDialog: false,
		secondDeleteDialog: false,
		deleteForm: {
			deleteReason: '',
			typeWord: '',
		},
		accountName: '',
		accountTimezone: '',
		localChannelAuth: [],
		inviteUserRole: undefined,
		isLoadingSave: false,
		disabledRoles: [AccountRoles.Admin],
	}),

	computed: {
		...mapGettersShorthand({
			account,
			getService,
			isAccountOwner,
			channelAuthInfo,
			hasAccountPermission,
			credByServiceVersionId,
			isNotificationsEnabled,
			dataLifetimeHumanReadable,
		}),
		orgName: {
			async set(value) {
				this.accountName = value;
			},
			get() {
				return this.accountName;
			},
		},
		timezonesArr() {
			return timezoneMoment.tz.names();
		},
		timezone: {
			set(value) {
				this.accountTimezone = value;
			},
			get() {
				return this.accountTimezone;
			},
		},
		canDelete() {
			return this.deleteForm.deleteReason && this.deleteForm.typeWord === 'DELETE';
		},
		hasPermissionToChangeAccount() {
			return this.hasAccountPermission(AccountPermissions.ChangeAccount);
		},
		slackInstallUrl() {
			return `${process.env.VUE_APP_API}integrations/slack/install`;
		},
	},
	methods: {
		...mapActionsShorthand({ fetchChannels, updateAccount, sendDeleteAccountMail, saveWorkspaceAvatar, getActiveAccount }),
		connectSlack() {
			const height = window.screen.availHeight - 300;
			const width = window.screen.availWidth - 300;
			const topBarHeight = outerHeight - innerHeight;
			const top = screen.availTop + screen.availHeight / 2 - height / 2 - topBarHeight / 2;
			const left = screen.availLeft + screen.availWidth / 2 - width / 2;
			window.open(this.slackInstallUrl, 'Connect Slack', `height=${height},width=${width}, top=${top}, left=${left}`);
		},
		async onFileSelected(file) {
			try {
				await this.saveWorkspaceAvatar({ avatar: file });
				this.$toast.success('Avatar uploaded');
			} catch (err) {
				this.$toast.error('Failed to upload avatar');
			}
		},
		async openDeleteWorkspace() {
			const dialogParams = {
				title: 'Delete Workspace',
				dangerText:
					'This will delete all assets in the workspace, including all features, connections, and team spaces. This will affect you and all users in your workspace. You cannot undo this action.',
				verificationDescription: 'Please type the name of the workspace to confirm.',
				noButtonText: 'Cancel',
				yesButtonText: 'Send confirmation via email',
				onYes: this.sendDeleteAccountMail,
				confirmationText: this.accountName,
				titleSuffixComponent: { component: AppWorkspaceAvatar, props: { account: this.account }, class: 'mb-6' },
				areYouSureText: `Are you sure you want to delete the workspace <h3 style="color: #f55366">&nbsp;${this.accountName}</h3> ?`,
			};
			const dialogRes = await AppDialogManagerService.openDangerConfirmationDialog(dialogParams);
			if (dialogRes === APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
				this.afterExitWorkspace();
			}
		},
		openLeaveWorkspace() {
			this.leaveWorkspace(this.account.id); //from mixin
		},
		afterExitWorkspace() {
			AppDialogManagerService.closeCurrentDialog(); //Close settings dialog
		},
		deactivateAccount() {
			this.deactivateDialog = false;
			console.log('deactivateAccount');
		},
		deactivateInstead() {
			this.firstDeleteDialog = false;
			this.deactivateDialog = true;
		},
		deleteForever() {
			this.secondDeleteDialog = false;
			console.log('deleteForever');
		},
		continueDelete() {
			this.firstDeleteDialog = false;
			this.secondDeleteDialog = true;
		},
		async save() {
			this.isLoadingSave = true;
			try {
				await this.updateAccount({
					name: this.accountName,
					timezone: this.accountTimezone,
					default_new_user_role: this.inviteUserRole,
					notification_channel_credentials: this.localChannelAuth.map(({ credId }) => credId).filter(Boolean),
				});
				this.$toast.success('Workspace settings saved.');
			} catch (e) {
				if (e?.response?.data?.error?.code === PERMISSION_DENIED_ERROR && e?.response?.data?.error?.msg) {
					this.$toast.error(e?.response.data.error.msg);
				} else {
					this.$toast.error('Something went wrong.');
					throw e;
				}
			} finally {
				this.isLoadingSave = false;
			}
		},
		onMessage(event) {
			const data = event.data;
			if (!data) {
				return;
			}
			if (data === SLACK_CONNECTED_EVENT) {
				this.getActiveAccount();
				this.$toast.success('Slack connected successfully');
			}
		},
		removeMessageListener() {
			if (this.messageHandler) {
				window.removeEventListener('message', this.messageHandler);
				this.messageHandler = null;
			}
		},
		listenOnMessage() {
			this.removeMessageListener();
			this.messageHandler = (e) => this.onMessage(e);
			bc.addEventListener('message', this.messageHandler);
		},
	},
	async mounted() {
		this.accountName = this.account.name;
		await this.fetchChannels();
		this.accountName = this.account.name;
		this.accountTimezone = this.account.timezone;
		this.localChannelAuth = this.channelAuthInfo.map((c) => {
			const cred = this.credByServiceVersionId(c.service_version_id);
			return {
				...c,
				name: this.getService(c.service)?.service_name,
				credId: cred?.id,
				credData: cred,
			};
		});
		this.inviteUserRole = this.account.default_new_user_role || AccountRoles.StandardEndUser;

		this.listenOnMessage();
	},
	beforeDestroy() {
		this.removeMessageListener();
	},
};
</script>

<style scoped lang="scss">
.short_name {
	font-size: 48px !important;
	color: white !important;
	position: relative;
}
.v-btn {
	font-size: 14px !important;
}
h3.semi {
	font-size: 14px;
	font-weight: 600 !important;
}
ul li {
	font-size: 14px;
	font-weight: 300;
	color: #292829;
}
.footer-container {
	border-top: 1px solid #e1e5f0;
}

.footer-button {
	width: 121px !important;
	height: 35px !important;
	font-size: 12px !important;
	border-radius: 6px !important;
	font-weight: normal !important;
	letter-spacing: 0px !important;
}
.delete-button {
	@extend .footer-button;
	color: #e84e53 !important;
	background: #fdedef !important;
}
.leave-button {
	@extend .footer-button;
	color: #44433f !important;
	background: var(--white) !important;
	border: 1px solid #d6ddea !important;
}

.explanation {
	font-size: 12px;
	color: var(--description-text);
}
</style>
